import React, {useEffect, useRef, useState} from "react";
import {omit} from "lodash";
import {useMutation} from "react-apollo";
import gql from "graphql-tag";
import {useTranslation} from 'react-i18next';
import {feltMediator} from "../../graphql/mutations";
import {getPlanFromAIChat} from "../../utils/helpers";
import './SuggestedIdeas.scss';
import EditModal from '../EditModal/EditModal';
// AI mediator generation moved to Recap step; keep only renderer helpers as needed
import { buildRelationshipDataTextFromJourney, buildFeltMediatorPrompt, callFeltMediator } from '../../utils/feltMediator';
import { preparePositions, fadeOutCard, fadeInCard, reArrangeCards, TRANSITION } from '../../utils/divTransitions';

const SuggestedIdeas = ({mirrorData, conflictData , Side2_Nice, name, partnerName, side1Animal, side1Gender, side2Animal, side2Gender, relation, updateValue}) => {

  const {i18n} = useTranslation();
  const {t} = useTranslation();
  console.log("SuggestedIdeas props:", {mirrorData, conflictData, Side2_Nice, name, partnerName, side1Animal, side1Gender, side2Animal, side2Gender, relation});
  console.log("🌐 Current language:", i18n.language);

  const relationshipDataForMediator = {
    mirrorData,
    conflictData,
    Side2_Nice,
    name,
    partnerName,
    side1Animal,
    side1Gender,
    side2Animal,
    side2Gender,
    relation,
  }
  

  const makeRelationshipDataReadableForMediator = (data) => {
    const text = buildRelationshipDataTextFromJourney({
      mirrorData,
      name,
      partnerName,
      side1Gender,
      side1Animal,
      side2Gender,
      side2Animal,
      relation
    });
    console.log(text);
    return text;
  };

  // --- Stable keys for animations ---
  const hashString = (str = '') => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      // eslint-disable-next-line no-bitwise
      h = (h * 33) ^ str.charCodeAt(i);
    }
    // eslint-disable-next-line no-bitwise
    return (h >>> 0).toString(16);
  };

  const getUserKey = (idea) => `user-${hashString(String(idea))}`;
  const getAiKey = (sug, fallbackIndex) => {
    try {
      const basis = `${sug?.emoji || ''}|${sug?.title || ''}|${sug?.description || ''}|${sug?.why || ''}`;
      const hashed = hashString(basis);
      return `ai-${hashed}`;
    } catch (e) {
      return `ai-${fallbackIndex}`;
    }
  };

  // removed: mediator prompt/response/generation; data comes from AISummary

  // Unified suggestions state
  const [suggestions, setSuggestions] = useState([]);
  // Persisted tombstones for deleted ideas so refetch/rehydration won't bring them back
  const [deletedKeys, setDeletedKeys] = useState(new Set());
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [editingIdeaIndex, setEditingIdeaIndex] = useState(null);
  const [editingIdeaType, setEditingIdeaType] = useState(null); // 'user' or 'ai'
  const [ideaText, setIdeaText] = useState('');

  // AI Suggestions State
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [mirrorName, setMirrorName] = useState(null);
  const [mirrorDescription, setMirrorDescription] = useState(null);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const hasSavedRef = useRef(false);

  // Load unified suggestions (migrate from old shape if needed)
  useEffect(() => {
    console.log('🔍 Checking for existing Do/Don\'ts...');
    console.log('🔍 mirrorData:', mirrorData);
    console.log('🔍 mirrorData.journeyProps:', mirrorData?.journeyProps);
    console.log('🔍 mirrorData.journeyProps.AISummary:', mirrorData?.journeyProps?.AISummary);
    if (mirrorData && mirrorData.journeyProps && mirrorData.journeyProps.AISummary) {
      const aiSummary = mirrorData.journeyProps.AISummary;
      console.log('📊 Checking AISummary in database:', aiSummary);
      // Load persisted tombstones (deleted suggestion keys)
      try {
        const persistedDeleted = Array.isArray(aiSummary?.deletedSuggestionKeys) ? aiSummary.deletedSuggestionKeys : [];
        setDeletedKeys(new Set(persistedDeleted));
      } catch (_) {}
      if (Array.isArray(aiSummary)) {
        const unifiedRaw = aiSummary.map(s => ({ ...s, type: s.type || 'aiSuggestion' }));
        const unified = unifiedRaw.filter((s, idx) => {
          const k = s.type === 'userSuggestion' ? getUserKey(s.description) : getAiKey(s, idx);
          return !deletedKeys.has(k);
        });
        setSuggestions(unified);
        setHasLoadedSuggestions(unified.length > 0);
      } else if (typeof aiSummary === 'object') {
        const ai = Array.isArray(aiSummary.suggestions) ? aiSummary.suggestions : [];
        const aiWithType = ai.map(s => ({ ...s, type: s.type || 'aiSuggestion' }));
        const user = Array.isArray(aiSummary.userSuggestions)
          ? aiSummary.userSuggestions.map(desc => ({ description: desc, type: 'userSuggestion' }))
          : [];
        const unifiedRaw = [...user, ...aiWithType];
        const unified = unifiedRaw.filter((s, idx) => {
          const k = s.type === 'userSuggestion' ? getUserKey(s.description) : getAiKey(s, idx);
          return !deletedKeys.has(k);
        });
        setSuggestions(unified);
        setHasLoadedSuggestions(unified.length > 0);
        if (aiSummary.mirrorName) setMirrorName(aiSummary.mirrorName);
        if (aiSummary.mirrorDescription) setMirrorDescription(aiSummary.mirrorDescription);
      }
    } else {
      console.log('📊 No AISummary found in database');
    }
  }, [mirrorData]);

  const cardsContainerRef = useRef(null);

  const handleAddUserIdea = () => {
    setEditingIdeaIndex(null);
    setEditingIdeaType('user');
    setIdeaText('');
    setShowIdeaModal(true);
  };

  const handleEditUserIdea = (index) => {
    setEditingIdeaIndex(index);
    setEditingIdeaType('user');
    const userItems = suggestions.filter(s => s.type === 'userSuggestion');
    setIdeaText(userItems[index]?.description || '');
    setShowIdeaModal(true);
  };

  const handleEditAIdea = (index) => {
    setEditingIdeaIndex(index);
    setEditingIdeaType('ai');
    const aiItems = suggestions.filter(s => s.type !== 'userSuggestion');
    setIdeaText(aiItems[index]?.description || '');
    setShowIdeaModal(true);
  };

  const handleSaveIdea = () => {
    if (ideaText.trim()) {
      if (editingIdeaType === 'user') {
        const container = cardsContainerRef.current;
        const before = preparePositions(container);
        const newUnified = [...suggestions];
        if (editingIdeaIndex !== null) {
          let userIdx = -1;
          for (let i = 0, seen = 0; i < newUnified.length; i++) {
            if (newUnified[i].type === 'userSuggestion') {
              if (seen === editingIdeaIndex) { userIdx = i; break; }
              seen++;
            }
          }
          if (userIdx !== -1) newUnified[userIdx] = { ...newUnified[userIdx], description: ideaText.trim() };
        } else {
          newUnified.unshift({ description: ideaText.trim(), type: 'userSuggestion' });
          setTimeout(() => {
            reArrangeCards(container, before, { duration: TRANSITION.MOVE_MS, stepDelay: TRANSITION.STEP_DELAY_MS });
            const newKey = getUserKey(ideaText.trim());
            setTimeout(() => fadeInCard(container, newKey), TRANSITION.STEP_DELAY_MS);
          }, 0);
        }
        setSuggestions(newUnified);
        if (updateValue) {
          const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
          updateValue({
            ...existingAISummary,
            suggestions: newUnified,
            userSuggestions: null
          });
        }
      }
    }
    setShowIdeaModal(false);
    setIdeaText('');
    setEditingIdeaIndex(null);
    setEditingIdeaType(null);
  };

  const handleCancelIdea = () => {
    setShowIdeaModal(false);
    setIdeaText('');
    setEditingIdeaIndex(null);
    setEditingIdeaType(null);
  };

  const handleDeleteIdea = () => {
    const container = cardsContainerRef.current;
    const before = preparePositions(container);

    let targetKey = null;
    let newUnified = [];

    if (editingIdeaType === 'user') {
      const userItems = suggestions.filter(s => s.type === 'userSuggestion');
      const target = userItems[editingIdeaIndex];
      targetKey = target ? getUserKey(target.description) : null;

      let seen = 0;
      newUnified = suggestions.filter(s => {
        if (s.type !== 'userSuggestion') return true;
        if (seen === editingIdeaIndex) { seen++; return false; }
        seen++;
        return true;
      });
    } else {
      const aiItems = suggestions.filter(s => s.type !== 'userSuggestion');
      const target = aiItems[editingIdeaIndex];
      targetKey = target ? getAiKey(target, editingIdeaIndex) : null;

      let seen = 0;
      newUnified = suggestions.filter(s => {
        if (s.type === 'userSuggestion') return true;
        if (seen === editingIdeaIndex) { seen++; return false; }
        seen++;
        return true;
      });
    }

    setSuggestions(newUnified);
    if (updateValue) {
      const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
      // Persist tombstone so future rehydrations don't revive the idea
      const nextDeleted = (() => {
        const arr = Array.from(deletedKeys);
        return targetKey && !arr.includes(targetKey) ? [...arr, targetKey] : arr;
      })();
      setDeletedKeys(new Set(nextDeleted));
      updateValue({
        ...existingAISummary,
        suggestions: newUnified,
        userSuggestions: null,
        deletedSuggestionKeys: nextDeleted
      });
    }

    setTimeout(() => {
      reArrangeCards(container, before, { duration: TRANSITION.MOVE_MS, stepDelay: TRANSITION.STEP_DELAY_MS });
    }, 0);

    if (targetKey) fadeOutCard(container, targetKey);

    setShowIdeaModal(false);
    setIdeaText('');
    setEditingIdeaIndex(null);
    setEditingIdeaType(null);
  };

  const handleGetMoreSuggestions = async () => {
    console.log('🔄 Getting more Do/Don\'ts...');
    setIsLoadingSuggestions(true);
    try {
      const { language } = i18n;
      const relationshipDataText = buildRelationshipDataTextFromJourney({
        mirrorData,
        name,
        partnerName,
        side1Gender,
        side1Animal,
        side2Gender,
        side2Animal,
        relation
      });
      const prompt = buildFeltMediatorPrompt({
        relationshipDataText,
        numberOfSuggestions: 3,
        existingSuggestions: suggestions.filter(s => s.type !== 'userSuggestion'),
        shouldGenerateMirrorNameAndDescription: false,
        language
      });
      const resp = await callFeltMediator({ prompt, numberOfSuggestions: 3, shouldGenerateMirrorNameAndDescription: false });
      const newAIAll = (resp?.suggestions || []).map(s => ({ ...s, type: 'aiSuggestion' }));
      // Filter out any suggestions that were previously deleted (tombstoned)
      const newAIFiltered = newAIAll.filter((s, idx) => !deletedKeys.has(getAiKey(s, idx)));
      const newUnified = [...suggestions, ...newAIFiltered];
      setSuggestions(newUnified);
      if (updateValue) {
        const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
        updateValue({
          ...existingAISummary,
          suggestions: newUnified
        });
      }
    } catch (error) {
      console.error('❌ Error getting more suggestions:', error);
    } finally {
      setIsLoadingSuggestions(false);
      console.log('🏁 Finished getting more Do/Don\'ts');
    }
  };

  // ---- Selection handling ----
  const findUnifiedIndex = (type, subsetIndex) => {
    let seen = 0;
    for (let i = 0; i < suggestions.length; i++) {
      const isUser = suggestions[i].type === 'userSuggestion';
      if ((type === 'user' && isUser) || (type !== 'user' && !isUser)) {
        if (seen === subsetIndex) return i;
        seen++;
      }
    }
    return -1;
  };

  const handleSelectIdea = (type, subsetIndex) => {
    const unifiedIndex = findUnifiedIndex(type, subsetIndex);
    if (unifiedIndex === -1) return;
    const newUnified = suggestions.map((s, idx) => ({ ...s, selected: idx === unifiedIndex }));
    setSuggestions(newUnified);
    if (updateValue) {
      const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
      updateValue({
        ...existingAISummary,
        suggestions: newUnified
      });
    }
  };

  const [feltMediatorMutation, {data, loading = true, error}] = useMutation(gql`${feltMediator}`);
  const [userAnswer, setUserAnswer] = useState();
  const [currentChat, setCurrentChat] = useState();
  const [shouldRestartChat, setShouldRestartChat] = useState(false);
  const bottomEl = useRef(null);

  const scrollToBottom = () => {
    bottomEl?.current?.scrollIntoView({behavior: 'smooth'});
  };

  const [LinesWrapperCount, setLinesWrapperCount] = useState(document.querySelectorAll('.linesWrapper').length);

  useEffect(() => {
    if (data) {
      setCurrentChat(data.feltMediator.AISummary);
    }
  }, [data])

  useEffect(() => {
    setLinesWrapperCount(document.querySelectorAll('.linesWrapper').length)
    if (currentChat)
      scrollToBottom()
  }, [currentChat, LinesWrapperCount])

  if (error){
    return <div> Something unexpected happened... please try again later </div>
  }

  console.log('🎨 RENDER - isLoadingSuggestions:', isLoadingSuggestions);
  console.log('🎨 RENDER - suggestions unified:', suggestions);
  console.log('🎨 RENDER - suggestions count:', suggestions.length);
  console.log('🎨 RENDER - hasLoadedSuggestions:', hasLoadedSuggestions);

  return (
    <div className="entireChat" style={{ direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}>
      {/* {mirrorName && <p className="conflict-name"><strong>{t('general.aiStep.whatHappenedTitle')}:</strong> {mirrorName}</p>}
      {mirrorDescription && <p className="conflict-description">{mirrorDescription}</p>} */}

      <div className="miniPanel">
      <label className="form-label noselect form-label-title">{t('suggestedIdeas.title')}</label>
      <label className="form-label noselect ">{t('suggestedIdeas.subtitle')}</label>
        <div className="miniPanel__buttons-container">
          <div className="miniPanel__button" onClick={handleAddUserIdea}>
            + {t('suggestedIdeas.add')}
          </div>
          {!isLoadingSuggestions ? <div className="miniPanel__button miniPanel__button-secondary" onClick={handleGetMoreSuggestions}>
          + {t('suggestedIdeas.generate')}
          </div>
          :
          (
            <div className="answerAI">
            <div className="typing">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
          )
          }
        </div>
      </div>

      <div className="ideasGallery" ref={cardsContainerRef}>
        <div className="ideas-grid">
          {suggestions.filter(s => s.type === 'userSuggestion').map((s, index) => (
            <div key={getUserKey(s.description)} data-key={getUserKey(s.description)} className={"idea-card" + (s.selected ? ' idea-card--selected' : '')} onClick={(e) => { e.stopPropagation(); handleSelectIdea('user', index); }}>
              <div className="idea-card-menu-icon" onClick={(e) => { e.stopPropagation(); handleEditUserIdea(index); }} aria-label="edit">
                <img src="/assets/icons/icon-3dots.svg" alt="menu" />
              </div>
              {s.selected ? <div className="idea-card-checkbox">
                    <img src="/assets/icons/v-green-22.svg" alt="menu" />
                  </div> : <div className="idea-card-checkbox"></div>}
              <div className="card-content">
                <div className="idea-card-text-container">
                    {/* <div className="idea-card-number">{index + 1}</div> */}
                  <p className="idea-card-description">{s.description}</p>
                  </div>
              </div>
            </div>
          ))}
          {suggestions.filter(s => s.type !== 'userSuggestion').map((suggestion, index) => (
                <div key={getAiKey(suggestion, index)} data-key={getAiKey(suggestion, index)} className={"idea-card" + (suggestion.selected ? ' idea-card--selected' : '') } onClick={(e) => { e.stopPropagation(); handleSelectIdea('ai', index); }}>
                  <div className="idea-card-menu-icon" onClick={(e) => { e.stopPropagation(); handleEditAIdea(index); }} aria-label="edit">
                    <img src="/assets/icons/icon-3dots.svg" alt="menu" />
                  </div>
                  {suggestion.selected ? <div className="idea-card-checkbox">
                    <img src="/assets/icons/v-green-22.svg" alt="menu" />
                  </div> : <div className="idea-card-checkbox"></div>}
                  <div className="card-content">
                    <div className="idea-card-text-container">
                    {/* <div className="idea-card-number">{index + 1 + suggestions.filter(s => s.type === 'userSuggestion').length}</div> */}
                    <span className="idea-card-emoji" aria-hidden="true">{suggestion.emoji}</span>
                    <h4 className="idea-card-title">{suggestion.title}</h4>
                    <p className="idea-card-description">{suggestion.description}</p>
                    <p className="idea-card-why"><strong>{t('suggestedIdeas.why')}:</strong> {suggestion.why}</p>
                    {/* <div className="idea-card-select">
                      {suggestion.selected ? '★' : '☆'}
                    </div> */}
                  </div>
                </div>
            </div>
          ))}
          {isLoadingSuggestions && <div className="idea-card idea-card__loading noselect">
              <div className="typing">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>}
        </div>
      </div>

      <EditModal
        isOpen={showIdeaModal}
        title={
          editingIdeaIndex !== null
            ? t('suggestedIdeas.edit')
                    : t('suggestedIdeas.add')
                  }
              value={ideaText}
        onChange={setIdeaText}
        onCancel={() => setShowIdeaModal(false)}
        onSave={handleSaveIdea}
        onDelete={handleDeleteIdea}
        showDelete={true}
        isReadOnly={editingIdeaType === 'ai'}
              placeholder={t('suggestedIdeas.placeholder', "Write your do or don't...")}
      />

      <div ref={bottomEl}></div>
    </div>
  )
}

export default SuggestedIdeas


